import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Hammer, Inbox, MessageSquareQuote, FolderKanban, FileText } from "lucide-react";

import LeadsAdmin from "@/pages/admin/LeadsAdmin";
import TestimonialsAdmin from "@/pages/admin/TestimonialsAdmin";
import ProjectsAdmin from "@/pages/admin/ProjectsAdmin";
import PostsAdmin from "@/pages/admin/PostsAdmin";

const TABS = [
  { id: "leads", label: "Leads", icon: Inbox, Component: LeadsAdmin },
  { id: "testimonials", label: "Testimonials", icon: MessageSquareQuote, Component: TestimonialsAdmin },
  { id: "projects", label: "Projects", icon: FolderKanban, Component: ProjectsAdmin },
  { id: "posts", label: "Blog", icon: FileText, Component: PostsAdmin },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("leads");

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const ActiveTab = TABS.find((t) => t.id === active)?.Component || LeadsAdmin;

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900" data-testid="admin-dashboard">
      <Helmet>
        <title>Admin Dashboard · Prime Build Design LTD</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <header className="bg-slate-950 text-white border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-home-link">
            <div className="w-9 h-9 bg-white text-slate-900 flex items-center justify-center"><Hammer size={18} strokeWidth={1.5} /></div>
            <div>
              <div className="font-display font-semibold text-sm">Prime Build Design</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-slate-400">Admin Portal</div>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-400 hidden md:block" data-testid="admin-current-user">{user?.email}</span>
            <button onClick={handleLogout} className="text-sm flex items-center gap-2 text-slate-300 hover:text-white" data-testid="admin-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Tab bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30" data-testid="admin-tabs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-1 overflow-x-auto">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActive(id)}
              data-testid={`admin-tab-${id}`}
              className={`px-5 py-4 text-sm font-medium tracking-wide whitespace-nowrap inline-flex items-center gap-2 border-b-2 transition-colors ${
                active === id
                  ? "border-[#C5A059] text-slate-900"
                  : "border-transparent text-slate-600 hover:text-slate-900"
              }`}
            >
              <Icon size={15} strokeWidth={1.6} /> {label}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <ActiveTab />
      </main>
    </div>
  );
}
